import type { AxiosError } from 'axios';

/**
 * Normalized API error. All HTTP failures funnel through `ApiError.from(...)`
 * (called by the axios response interceptor) so call sites have one shape
 * to pattern-match against:
 *
 *   try { await api.get(...) } catch (e) {
 *     if (e instanceof ApiError && e.status === 409) ...
 *   }
 *
 * Mirrors the legacy `ApiException` in @propertyscan/shared/api so the
 * server-action → client-mutation migration can keep the same status checks.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly code: string;
    readonly details?: unknown;
    override readonly cause?: unknown;

    constructor(args: { status: number; code: string; message: string; details?: unknown; cause?: unknown }) {
        super(args.message, args.cause !== undefined ? { cause: args.cause } : undefined);
        this.name = 'ApiError';
        this.status = args.status;
        this.code = args.code;
        if (args.details !== undefined) this.details = args.details;
        if (args.cause !== undefined) this.cause = args.cause;
    }

    /** True for connectivity failures (server down, DNS, CORS, offline). */
    get isNetwork(): boolean {
        return this.status === 0;
    }

    /** True for auth failures the app should respond to by signing the user out. */
    get isUnauthorized(): boolean {
        return this.status === 401 || this.status === 403;
    }

    /** Per-field messages from a 400 (`errors: [{ field, message }]`), if any. */
    get fieldErrors(): Array<{ field: string; message: string }> {
        if (!Array.isArray(this.details)) return [];
        return this.details.filter(
            (e): e is { field: string; message: string } =>
                !!e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string',
        );
    }

    /** The message to actually show a user. `message` alone is the envelope's
     *  generic "Validation failed" on a 400 — the useful text is per-field. */
    get displayMessage(): string {
        const fields = this.fieldErrors;
        return fields.length ? fields.map((e) => e.message).join(' ') : this.message;
    }

    /** Build an ApiError from any axios failure. Surfaces the new envelope
     *  `{ success: false, message, error, errors?, details? }` from
     *  apps/api/src/middlewares/error.middleware.ts. Validation errors
     *  arrive as `{ errors: [{ field, message }] }`; they're stored on
     *  `details` so callers can render per-field messages. */
    static from(err: unknown): ApiError {
        if (err instanceof ApiError) return err;

        const axiosErr = err as AxiosError<ApiErrorBody>;
        if (axiosErr?.isAxiosError) {
            // No response → network/CORS failure. Surface a stable status=0.
            if (!axiosErr.response) {
                return new ApiError({
                    status: 0,
                    code: axiosErr.code ?? 'network_error',
                    message: friendlyNetworkMessage(axiosErr),
                    cause: axiosErr,
                });
            }
            const { status, data, statusText } = axiosErr.response;
            const message = data?.message ?? data?.error ?? statusText ?? `Request failed (${status})`;
            const details = data?.errors ?? data?.details;
            return new ApiError({
                status,
                code: data?.error ?? 'unknown',
                message,
                details,
                cause: axiosErr,
            });
        }

        if (err instanceof Error) {
            return new ApiError({ status: 0, code: 'unknown', message: err.message, cause: err });
        }
        return new ApiError({ status: 0, code: 'unknown', message: 'Unknown error', cause: err });
    }
}

interface ApiErrorBody {
    success?: false;
    message?: string;
    error?:   string;
    errors?:  Array<{ field: string; message: string }>;
    details?: unknown;
}

/** Plain-language hint for the common "API is down in dev" case. */
function friendlyNetworkMessage(err: AxiosError): string {
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') {
        return "Couldn't reach the server. The API may not be running — start the stack with `pnpm dev`, then try again.";
    }
    return err.message || 'Network request failed';
}
